import React, { useState, useRef } from 'react';
import { AlertTriangle, User, Upload } from 'lucide-react';
import { Warning } from '../../types/types';
import useProfileSettings from '../../hooks/useProfileSettings';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import Input from '../ui/input';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { uploadProfilePicture, updateProfilePicture } from '../../services/imageService';
import { Progress } from '../ui/progress';

/**
 * Component for handling profile picture uploads and display
 */
const ProfilePicture = ({
  userData,
  onUpdateSuccess,
  onError,
}: {
  userData: { username: string; profilePicture?: string };
  onUpdateSuccess: (message: string) => void;
  onError: (message: string) => void;
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFullImage, setShowFullImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const downloadURL = await uploadProfilePicture(userData.username, file, progress =>
        setUploadProgress(progress),
      );

      await updateProfilePicture(userData.username, downloadURL);

      onUpdateSuccess('Profile picture updated successfully!');
    } catch (error) {
      onError(`Error uploading profile picture: ${(error as Error).message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <p className='text-sm font-medium'>Profile Picture</p>
      </div>

      <div className='flex flex-col items-center space-y-4'>
        {/* Profile picture display */}
        <div
          className='relative cursor-pointer group'
          onClick={() => userData.profilePicture && setShowFullImage(true)}>
          {userData.profilePicture ? (
            <img
              src={userData.profilePicture}
              alt='Profile'
              className='w-32 h-32 rounded-full object-cover border-2 border-gray-200'
            />
          ) : (
            <div className='w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200'>
              <User className='h-16 w-16 text-gray-400' />
            </div>
          )}

          {userData.profilePicture && (
            <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all'>
              <span className='text-white opacity-0 group-hover:opacity-100'>View</span>
            </div>
          )}
        </div>

        {/* Upload controls */}
        {!uploading ? (
          <div className='flex space-x-2'>
            <input
              type='file'
              accept='image/*'
              className='hidden'
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <Button variant='outline' size='sm' onClick={handleUploadClick} disabled={uploading}>
              <Upload className='h-4 w-4 mr-2' />
              Upload New Picture
            </Button>
          </div>
        ) : (
          <div className='w-full max-w-xs'>
            <p className='text-xs text-center mb-2'>Uploading... {Math.round(uploadProgress)}%</p>
            <Progress value={uploadProgress} />
          </div>
        )}

        {/* Image preview dialog */}
        <Dialog open={showFullImage} onOpenChange={setShowFullImage}>
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>Profile Picture</DialogTitle>
              <DialogDescription>{userData.username}&apos;s profile picture</DialogDescription>
            </DialogHeader>
            <div className='flex justify-center p-4'>
              <img
                src={userData.profilePicture}
                alt='Profile'
                className='max-h-96 object-contain'
              />
            </div>
            <DialogFooter>
              <Button type='button' onClick={() => setShowFullImage(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const ProfileSettings: React.FC = () => {
  const {
    userData,
    loading,
    editBioMode,
    editEmailMode,
    newBio,
    newEmail,
    newPassword,
    confirmNewPassword,
    successMessage,
    errorMessage,
    showConfirmation,
    pendingAction,
    canEditProfile,
    showPassword,
    isProviderUser,
    getProviderName,

    setEditBioMode,
    setEditEmailMode,
    setNewBio,
    setNewEmail,
    setNewPassword,
    setConfirmNewPassword,
    setShowConfirmation,
    setSuccessMessage,
    setErrorMessage,

    handleResetPassword,
    handleUpdateBiography,
    handleUpdateEmail,
    handleDeleteUser,

    handleToggleVisibility,
  } = useProfileSettings();

  if (loading) {
    return (
      <div className='container flex items-center justify-center min-h-screen'>
        <Card className='w-[600px]'>
          <CardContent className='pt-6'>
            <p className='text-center text-muted-foreground'>Loading user data...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container flex items-center justify-center min-h-screen py-8'>
      <Card className='w-[600px]'>
        <CardHeader>
          <div className='flex justify-between items-center'>
            <CardTitle>Profile Settings</CardTitle>
          </div>
        </CardHeader>
        <CardContent className='space-y-6'>
          {successMessage && (
            <Alert variant='default' className='bg-green-50 text-green-900 border-green-200'>
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}
          {errorMessage && (
            <Alert variant='destructive'>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          {userData ? (
            <>
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold'>General Information</h3>

                {/* Add ProfilePicture component here */}
                <ProfilePicture
                  userData={userData}
                  onUpdateSuccess={setSuccessMessage}
                  onError={setErrorMessage}
                />

                <div className='space-y-2'>
                  <p className='text-sm font-medium'>Username</p>
                  <p className='text-sm text-muted-foreground'>{userData.username}</p>
                  {isProviderUser && (
                    <p className='text-xs italic text-amber-600'>
                      This is a {getProviderName()}-linked account. Some settings cannot be changed
                      here.
                    </p>
                  )}
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <p className='text-sm font-medium'>Email Address</p>
                    {canEditProfile && !editEmailMode && !isProviderUser && (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          setEditEmailMode(true);
                          setNewEmail(userData.email || '');
                        }}>
                        Edit
                      </Button>
                    )}
                  </div>
                  {!editEmailMode ? (
                    <p className='text-sm text-muted-foreground'>
                      {userData.email || 'No email address set.'}
                    </p>
                  ) : (
                    <div className='flex items-center space-x-2'>
                      <Input
                        type='email'
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        placeholder='Enter your email address'
                      />
                      <Button size='sm' onClick={handleUpdateEmail}>
                        Save
                      </Button>
                      <Button variant='outline' size='sm' onClick={() => setEditEmailMode(false)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <p className='text-sm font-medium'>Biography</p>
                    {canEditProfile && !editBioMode && (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          setEditBioMode(true);
                          setNewBio(userData.biography || '');
                        }}>
                        Edit
                      </Button>
                    )}
                  </div>
                  {!editBioMode ? (
                    <p className='text-sm text-muted-foreground'>
                      {userData.biography || 'No biography set.'}
                    </p>
                  ) : (
                    <div className='space-y-2'>
                      <textarea
                        className='w-full p-2 border rounded-md resize-none'
                        rows={4}
                        value={newBio}
                        onChange={e => setNewBio(e.target.value)}
                        placeholder='Write something about yourself...'
                      />
                      <div className='flex space-x-2'>
                        <Button size='sm' onClick={handleUpdateBiography}>
                          Save
                        </Button>
                        <Button variant='outline' size='sm' onClick={() => setEditBioMode(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className='space-y-2'>
                  <p className='text-sm font-medium'>Date Joined</p>
                  <p className='text-sm text-muted-foreground'>
                    {userData.dateJoined
                      ? new Date(userData.dateJoined).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <p className='text-sm font-medium'>Profile Visibility</p>
                    {canEditProfile && (
                      <Button variant='outline' size='sm' onClick={handleToggleVisibility}>
                        Make {userData.visibility === 'public' ? 'Private' : 'Public'}
                      </Button>
                    )}
                  </div>
                  <div className='flex items-center'>
                    <span
                      className={`text-sm text-muted-foreground ${
                        userData?.visibility === 'public' ? 'public' : 'private'
                      }`}>
                      {userData?.visibility === 'public'
                        ? 'Public: Anyone can view'
                        : 'Private: Only you can view'}
                    </span>
                  </div>
                  <div className='flex space-x-2'>
                    <Button variant='outline' onClick={handleToggleVisibility}>
                      {userData?.visibility === 'public' ? 'Make Private' : 'Make Public'}
                    </Button>
                  </div>
                </div>
                {/* Display warnings if the user has any */}
                {userData.hasWarnings && userData.warnings && userData.warnings.length > 0 && (
                  <div className='pt-4 border-t space-y-2'>
                    <div className='flex items-center space-x-2'>
                      <AlertTriangle className='h-4 w-4 text-amber-500' />
                      <p className='text-sm font-medium text-amber-700'>Account Warnings</p>
                    </div>
                    <div className='bg-amber-50 border border-amber-200 rounded-md p-4'>
                      <p className='text-xs text-amber-700 mb-2'>
                        Your account has received the following warnings:
                      </p>
                      <div className='space-y-3'>
                        {userData.warnings.map((warning: Warning, index: number) => (
                          <div
                            key={index}
                            className='border-b border-amber-200 pb-2 last:border-0 last:pb-0'>
                            <p className='text-sm font-medium text-amber-800'>{warning.reason}</p>
                            <div className='text-xs text-amber-600 mt-1 flex justify-between'>
                              <span>Issued on {new Date(warning.date).toLocaleDateString()}</span>
                              <span>By: {warning.issuedBy}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className='text-xs text-amber-800 mt-3 font-medium'>
                        Multiple or severe warnings may lead to account suspension. Please review
                        our community guidelines.
                      </p>
                    </div>
                  </div>
                )}

                {canEditProfile && (
                  <>
                    {!isProviderUser && (
                      <div className='space-y-4 pt-4 border-t'>
                        <h3 className='text-lg font-semibold'>Reset Password</h3>
                        <div className='space-y-2'>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder='New Password'
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                          />
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder='Confirm New Password'
                            value={confirmNewPassword}
                            onChange={e => setConfirmNewPassword(e.target.value)}
                          />
                          <div className='flex space-x-2'>
                            <Button variant='outline' onClick={handleToggleVisibility}>
                              {showPassword ? 'Hide Passwords' : 'Show Passwords'}
                            </Button>
                            <Button onClick={handleResetPassword}>Reset Password</Button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className='space-y-4 pt-4 border-t'>
                      <h3 className='text-lg font-semibold text-destructive'>Danger Zone</h3>
                      <Button variant='destructive' onClick={handleDeleteUser}>
                        Delete This User
                      </Button>
                      {isProviderUser && (
                        <p className='text-xs italic text-amber-600 mt-2'>
                          Note: {getProviderName()}-linked accounts can only be fully deleted
                          through {getProviderName()}. This will only remove your account from our
                          service.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <p className='text-center text-muted-foreground'>User not found.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Account Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this account? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowConfirmation(false)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={() => pendingAction && pendingAction()}>
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileSettings;
